import {CommonModule} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {of} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';

import {MaterialModule} from '../../../material.module';
import {SyncHistory} from '../../../models/hubspot.model';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {ClientService} from '../../../services/auth/client.service';
import {HubspotService} from '../../../services/enrich/hubspot.service';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich';

@Component({
  selector: 'app-enrich-sync-history-detail',
  templateUrl: './sync-history-detail.component.html',
  styleUrls: ['./sync-history-detail.component.scss'],
  host: {class: 'webinars-m3 webinars-shell'},
  standalone: true,
  imports: [CommonModule, MaterialModule, BreadcrumbsComponent]
})
export class EnrichSyncHistoryDetailComponent implements OnInit {
  private hubspotService = inject(HubspotService);
  private clientService = inject(ClientService);
  private clientLoadingService = inject(ClientLoadingService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);

  syncHistory: SyncHistory | null = null;
  isLoading = false;
  error: string | null = null;

  readonly changeColumns: string[] = ['field', 'oldValue', 'newValue'];

  ngOnInit(): void {
    const syncHistoryId = this.route.snapshot.paramMap.get('id');
    if (!syncHistoryId) {
      this.error = 'Missing sync history id';
      return;
    }

    const navigationState = this.router.getCurrentNavigation()?.extras.state as {syncHistory?: unknown} | undefined;
    const historyState = history.state as {syncHistory?: unknown} | undefined;
    const stateValue = navigationState?.syncHistory ?? historyState?.syncHistory;
    const stateId = getSyncHistoryId(stateValue);
    if (stateId && stateId === syncHistoryId) {
      this.syncHistory = normalizeSyncHistory(stateValue, syncHistoryId);
      this.isLoading = false;
      this.error = null;
    }

    this.error = null;

    this.clientLoadingService.ensureClientSelected().subscribe({
      next: () => {
        this.loadSyncHistory(syncHistoryId, {silent: Boolean(this.syncHistory)});
      },
      error: err => {
        this.enrichDiagnostics.reportError('enrich.sync-history-detail.client-selection', err);
        if (!this.syncHistory) {
          this.error = 'No client selected. Please select a client from the dropdown.';
          this.isLoading = false;
        }
      }
    });

    this.isLoading = !this.syncHistory;
  }

  get breadcrumbs(): Array<{label: string; route?: string}> {
    const label = this.syncHistory?.companyName || 'All companies';
    return [
      {label: 'Shine Enrich', route: '/enrich/setup'},
      {label: 'Company data', route: '/enrich/companies'},
      {label: 'Sync history', route: '/enrich/sync-history'},
      {label: this.syncHistory ? label : 'Sync details'}
    ];
  }

  getStatusClass(status: SyncHistory['status']): string {
    switch (status) {
      case 'success':
        return 'status-success';
      case 'failed':
        return 'status-failed';
      case 'partial':
        return 'status-partial';
      case 'in_progress':
        return 'status-in-progress';
    }
  }

  statusIcon(status: SyncHistory['status']): string {
    switch (status) {
      case 'success':
        return 'check_circle';
      case 'failed':
        return 'cancel';
      case 'partial':
        return 'warning';
      case 'in_progress':
        return 'schedule';
    }
  }

  statusLabel(status: SyncHistory['status']): string {
    switch (status) {
      case 'success':
        return 'Success';
      case 'failed':
        return 'Failed';
      case 'partial':
        return 'Partial';
      case 'in_progress':
        return 'In progress';
    }
  }

  formatSyncType(syncType: SyncHistory['syncType']): string {
    switch (syncType) {
      case 'manual':
        return 'Manual';
      case 'scheduled':
        return 'Scheduled';
      case 'filter':
        return 'Filter';
      case 'individual':
        return 'Individual';
      case 'daily':
        return 'Daily';
      case 'import':
        return 'Import';
    }
  }

  private loadSyncHistory(id: string, options: {silent?: boolean} = {}): void {
    const silent = options.silent === true;

    const clientId = this.clientService.client?.id;
    if (!clientId) {
      if (!this.syncHistory) {
        this.error = 'No client selected';
        this.isLoading = false;
      }
      return;
    }

    if (!silent) {
      this.isLoading = true;
    }

    this.hubspotService
      .getSyncHistoryById(id, clientId)
      .pipe(
        catchError(error => {
          this.enrichDiagnostics.reportError('enrich.sync-history-detail.load', error);
          if (!this.syncHistory) {
            this.error = error instanceof Error && error.message === 'Sync history not found'
              ? 'Sync history not found'
              : 'Failed to load sync history';
          }
          return of(null);
        }),
        finalize(() => {
          if (!silent) {
            this.isLoading = false;
          }
        })
      )
      .subscribe(history => {
        if (!history) {
          return;
        }

        this.syncHistory = history;
      });
  }
}

function getSyncHistoryId(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const id = (value as any).id as unknown;
  if (typeof id === 'string' && id.length > 0) return id;
  if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  return null;
}

function normalizeSyncHistory(value: unknown, id: string): SyncHistory {
  if (!value || typeof value !== 'object') {
    return {id, status: 'failed', startedAt: new Date().toISOString(), syncType: 'manual'};
  }
  return {...(value as any), id} as SyncHistory;
}
